<template>
  <div class="min-h-screen bg-[#020617] text-slate-200 font-sans p-6 flex flex-col items-center justify-center relative overflow-hidden no-scrollbar">
    
    <div class="absolute inset-0 z-0">
      <div v-for="n in 5" :key="n" class="shooting-star"></div>
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px]"></div>
      <div class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-30"></div>
    </div>

    <div class="w-full max-w-2xl z-10">
      
      <div v-if="stage === 'welcome'" class="text-center space-y-12 animate-fade-in">
        <div class="space-y-4">
          <h1 class="text-5xl md:text-7xl font-black tracking-[0.2em] text-white opacity-90 uppercase">
            Star<span class="text-indigo-500">·</span>BTI
          </h1>
          <div class="h-[1px] w-24 bg-gradient-to-r from-transparent via-indigo-500 to-transparent mx-auto"></div>
          <p class="text-lg text-slate-400 font-light tracking-[0.3em] uppercase">宇宙能量匹配报告</p>
        </div>
        
        <div class="py-8 border-y border-white/5 space-y-4">
          <p class="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">
            在百亿光年的广袤宇宙中<br/>寻找属于你的星体
          </p>
        </div>

        <button @click="stage = 'testing'" class="group relative px-16 py-5 bg-transparent border border-white/20 rounded-full overflow-hidden transition-all hover:border-indigo-400">
          <div class="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
          <span class="relative z-10 text-white tracking-[0.5em] font-light">开始测试</span>
        </button>
      </div>

      <div v-else-if="stage === 'testing'" class="space-y-6 animate-fade-in">
        <div class="flex justify-between items-end px-2">
          <span class="text-[10px] tracking-[0.4em] text-indigo-400 uppercase">测试进度</span>
          <span class="text-3xl font-light italic">{{ currentQuestionIndex + 1 }}<span class="text-slate-700 text-sm not-italic">/{{ questions.length }}</span></span>
        </div>
        <div class="h-[2px] bg-slate-900 w-full rounded-full">
          <div class="h-full bg-indigo-400 shadow-[0_0_15px_rgba(129,140,248,0.5)] transition-all duration-700" :style="{ width: ((currentQuestionIndex + 1) / questions.length) * 100 + '%' }"></div>
        </div>
        
        <div class="py-8 flex items-center">
          <h2 class="text-xl md:text-2xl font-light leading-snug tracking-wide text-white">
            {{ questions[currentQuestionIndex].text }}
          </h2>
        </div>

        <div class="grid grid-cols-1 gap-3" :key="currentQuestionIndex">
          <button v-for="(weight, label) in optionWeights" :key="label" @click="handleAnswer(weight)"
            class="group text-left p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.08] hover:border-indigo-500/30">
            <span class="text-slate-400 group-hover:text-white transition-colors tracking-wide text-sm md:text-base">{{ label }}</span>
          </button>
        </div>
      </div>

      <div v-else-if="stage === 'calculating'" class="text-center space-y-8 animate-pulse">
        <div class="relative w-24 h-24 mx-auto">
          <div class="absolute inset-0 border-2 border-indigo-500/20 rounded-full"></div>
          <div class="absolute inset-0 border-t-2 border-indigo-500 rounded-full animate-spin"></div>
        </div>
        <p class="text-indigo-400 tracking-[0.4em] text-xs uppercase">{{ loadingText }}</p>
      </div>

      <div v-else-if="stage === 'result'" class="animate-fade-in space-y-6 text-center pb-12">
        
        <div class="relative w-48 h-48 mx-auto mb-4 group">
          <div class="absolute inset-4 bg-indigo-500/20 rounded-full blur-3xl animate-pulse z-0"></div>
          <img :src="resultData.image" class="w-full h-full object-cover rounded-xl filter drop-shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-transform duration-1000 group-hover:scale-105" />
        </div>

        <div class="space-y-2">
          <p class="text-indigo-400 tracking-[0.4em] uppercase text-[10px]">你在宇宙中的身份是</p>
          <h1 class="text-5xl md:text-6xl font-black text-white tracking-widest">{{ resultData.name }}</h1>
        </div>

        <div class="flex justify-center gap-2 py-2 flex-wrap">
          <div v-for="trait in sortedTraits.slice(0,3)" :key="trait.name" class="px-2 py-0 bg-white/5 rounded-full border border-white/10">
            <span class="text-xs text-slate-500 uppercase mr-2">{{ trait.name }}</span>
            <span class="text-xs text-indigo-400 font-mono">{{ trait.score }}%</span>
          </div>
        </div>

        <div class="p-6 rounded-[3rem] bg-white/[0.03] border border-white/10 backdrop-blur-3xl relative overflow-hidden">
          <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent"></div>
          <p class="text-lg leading-relaxed text-slate-300 font-light italic">
            "{{ resultData.desc }}"
          </p>
        </div>
          
        <div class="flex flex-col gap-2 w-24 h-30 mx-auto">
          <img src="/celestial/qrcode.png" class="w-24 h-24 rounded-xl" />
          <p class="text-indigo-400 tracking-[0.4em] uppercase text-[10px]">扫码参与测试</p>
        </div> 

        <div class="flex flex-row items-center justify-center gap-8">
          <button @click="resetTest" class="text-slate-500 hover:text-white text-xs tracking-widest uppercase border-b border-slate-800 pb-1 transition-all">
            重新测试
          </button>
          <router-link to="/?country=CN" class="text-slate-500 hover:text-white text-xs tracking-widest uppercase border-b border-slate-800 pb-1 transition-all">
            回到主页
          </router-link>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// 选项权重：从“完全不同意”到“完全同意”
const optionWeights = {
  "完全一致": 20,
  "比较符合": 15,
  "尚不明确": 10,
  "不太符合": 5,
  "绝不如此": 0
};

// 大五人格映射题目（5维度 x 3道题 = 15道）
// O:开放性, C:尽责性, E:外向性, A:宜人性, N:神经质(敏感度)
const rawQuestions = [
  // --- O (奇点活跃度 / 开放性) ---
  { id: 1, dim: 'O', text: "如果一段旋律能让你联想到某种颜色或形状，你会觉得这理所当然，而不是荒诞不经吗？" },
  { id: 2, dim: 'O', text: "进入一家从未去过的书店，你通常会直接走向那些名字稀奇古怪、甚至不知道在讲什么的神秘分区吗？" },
  { id: 3, dim: 'O', text: "你愿意相信：这个世界的运行逻辑逻辑其实像冰山一样，我们看到的只是那露出水面的、极其无趣的 10%。" },

  // --- C (轨道稳定性 / 尽责性) ---
  { id: 4, dim: 'C', text: "在开始一项复杂的任务前，你必须先把桌面、文档命名或是周围的环境整理到“绝对顺眼”的状态吗？" },
  { id: 5, dim: 'C', text: "如果一份攻略显示某家餐厅 21:00 停止营业，你会因为担心 20:45 赶到会打乱对方的节奏，而放弃前往吗？" },
  { id: 6, dim: 'C', text: "比起“随时出发”的惊喜，你更沉迷于一切尽在掌控的秩序感。" },

  // --- E (绝对星等 / 外向性) ---
  { id: 7, dim: 'E', text: "在热闹的派对或聚会中，你是否常觉得自己总是不自觉地想要吸引周围人的关注？" },
  { id: 8, dim: 'E', text: "长时间不与外界交换新鲜的谈资或能量，你的核心电量会迅速进入枯竭状态吗？" },
  { id: 9, dim: 'E', text: "在人群中保持沉默，对你来说比大声演说更累。" },

  // --- A (引力包容度 / 宜人性) ---
  { id: 10, dim: 'A', text: "当身边的人情绪低落时，你即便不说话，内心也会同步感受到对方那种沉重或压抑吗？" },
  { id: 11, dim: 'A', text: "如果一个陌生人在深夜请求你帮忙指路，即便你正赶时间，你也很难说出那个“不”字？" },
  { id: 12, dim: 'A', text: "你更愿意做那个温和的守护者，而不是在竞争中一定要赢过别人的胜利者。" },

  // --- N (光谱偏移率 / 敏感度) ---
  { id: 13, dim: 'N', text: "如果对方的回复里少了一个常用的表情包，你的内心是否会瞬间脑补出一场关于“我做错了什么”的星际风暴？" },
  { id: 14, dim: 'N', text: "在某个寂静的瞬间，你会被那种“万物终将消逝”的虚无感猛然击中，甚至产生生理上的压抑吗？" },
  { id: 15, dim: 'N', text: "你对周围环境的“不对劲”极其敏锐——哪怕只是对方说话的一个语气词变了，你也会察觉到。" }
];

const questions = rawQuestions.sort(() => Math.random() - 0.5);

// 16+1 映射矩阵
const mappingTable = {
  // --- O (奇点活跃度 / 开放性) 主导 ---
  "OE": { name: "超新星", image: "/celestial/supernova.jpg", desc: "极致的生命绽放。你瞬间爆发出巨大的创意能量，将斑斓元素洒向整个深空。" },
  "ON": { name: "虫洞", image: "/celestial/wormhole.jpg", desc: "连接维度的捷径。你讨厌陈规，总能在逻辑的褶皱中跳跃出全新的思维维度。" },
  "OA": { name: "弥漫星云", image: "/celestial/nebula.jpg", desc: "恒星的摇篮。你用柔软的色彩编织梦境，在静谧中孕育着惊人的生命力。" },
  "OC": { name: "类星体", image: "/celestial/quasar.jpg", desc: "重塑星系的能量核心。你拥有极高的探索深度，是宇宙规则的重新定义者。" },

  // --- C (轨道稳定性 / 尽责性) 主导 ---
  "CA": { name: "地球", image: "/celestial/earth.webp", desc: "万物的基石。你拥有坚硬的核心和精准的轨道，是宇宙中最值得信赖的锚点。" },
  "CN": { name: "白矮星", image: "/celestial/whitedwarf.jpg", desc: "极致自律的高密度存在。坚韧、高效、冷峻，余热在千亿年间恒久不散。" },
  "CE": { name: "主序恒星", image: "/celestial/sun.webp", desc: "持续供能的温暖源泉。你用自己的引力维系秩序，用光芒指引他人的航程。" },

  // --- E (绝对星等 / 外向性) 主导 ---
  "EA": { name: "联星系统", image: "/celestial/binary.webp", desc: "宇宙的社交家。在紧密的引力纽带中与他者协同旋转，共同照亮双星系统。" },
  "EC": { name: "土星环", image: "/celestial/saturn.jpg", desc: "宇宙的盛装舞会。你天生引人瞩目，在华丽的颗粒共振中展示生命的绚烂。" },
  "EN": { name: "流星", image: "/celestial/meteor.jpg", desc: "划破长夜的利刃。你追求极致的速度与瞬时的爆发，在燃烧中释放全部的热情。" },

  // --- A (引力包容度 / 宜人性) 主导 ---
  "AN": { name: "月球", image: "/celestial/moon.jpg", desc: "守望者的温柔。你默默陪伴，在最深沉的黑夜中提供最稳定的潮汐与月光。" },
  "AO": { name: "极光", image: "/celestial/aurora.jpg", desc: "随风而舞的艺术品。你是能量碰撞后的偶然奇迹，灵动、自由且无法被捕捉。" },

  // --- N (光谱偏移率 / 敏感度) 主导 ---
  "NC": { name: "黑洞", image: "/celestial/blackhole.webp", desc: "改变时空率的深邃灵魂。外表静谧，内心却拥有足以吞噬并转化万物的巨大能量。" },
  "NE": { name: "脉冲星", image: "/celestial/pulsar.jpg", desc: "宇宙的灯塔。你按照自己的频率精准闪烁，对外界信号拥有极强的穿透力。" },

  // --- 独特低分/特殊组合 (补充剩余图片) ---
  "LOW": { name: "小行星", image: "/celestial/asteroid.webp", desc: "沉默的独行侠。你拥有坚硬的内核，在荒芜的宇宙中探索独属于自己的轨迹。" },
  "HIDDEN": { name: "暗物质", image: "/celestial/darkmatter.jpg", desc: "宇宙的隐形架构。你虽不常被直观观测，却在逻辑底层决定了整个星系的运行方向。" },
};

const stage = ref('welcome');
const currentQuestionIndex = ref(0);
const scores = ref({ O: 0, C: 0, E: 0, A: 0, N: 0 });
const loadingText = ref('正在校准光谱偏移...');
const finalResultKey = ref('');

// 计算得分百分比
const sortedTraits = computed(() => {
  const traitNames = { O: '奇点活跃', C: '轨道稳定', E: '耀眼光芒', A: '引力包容', N: '光谱偏移' };
  return Object.keys(scores.value).map(key => ({
    key: key,
    name: traitNames[key],
    score: Math.round((scores.value[key] / 60) * 100) // 每维度最高60分
  })).sort((a, b) => b.score - a.score);
});

const resultData = computed(() => {
  return mappingTable[finalResultKey.value] || mappingTable["CA"];
});

const handleAnswer = (weight) => {
  const dim = questions[currentQuestionIndex.value].dim;
  scores.value[dim] += weight;

  if (currentQuestionIndex.value < questions.length - 1) {
    currentQuestionIndex.value++;
  } else {
    runCalculation();
  }
};

const runCalculation = () => {
  stage.value = 'calculating';
  const loadingTexts = ['正在匹配星系数据库...', '完成星系成分扫描'];
  let i = 0;
  const timer = setInterval(() => {
    loadingText.value = loadingTexts[i++];
    if (i === loadingTexts.length) {
      clearInterval(timer);

      const s = sortedTraits.value; // 已按分数从高到低排序
      const top1 = s[0];
      const top2 = s[1];

      // 转换函数
      const k1 = top1.key;
      const k2 = top2.key;

      // 2. 判定暗物质 (低外向度 E + 高开放度 O)
      if (scores.value.E < 30 && scores.value.O > 70) {
        finalResultKey.value = "HIDDEN";
      }
      // 3. 判定小行星 (所有维度分值普遍偏低，独立性极强)
      else if (top1.score < 45) {
        finalResultKey.value = "LOW";
      }
      // 4. 正常组合逻辑
      else {
        const combo = k1 + k2;
        // 优先匹配 Top1+Top2 的特定组合
        if (mappingTable[combo]) {
          finalResultKey.value = combo;
        } else if (mappingTable[k2 + k1]) {
          finalResultKey.value = k2 + k1;
        } else {
          // 如果没有特定组合，根据 Top1 强制分配一个结果
          const fallback = { 'O': 'OE', 'C': 'CA', 'E': 'EC', 'A': 'AN', 'N': 'NC' };
          finalResultKey.value = fallback[k1] || "CA";
        }
      }
      
      stage.value = 'result';
    }
  }, 1000);
};

const resetTest = () => {
  stage.value = 'welcome';
  currentQuestionIndex.value = 0;
  scores.value = { O: 0, C: 0, E: 0, A: 0, N: 0 };
};
</script>

<style scoped>
/* 核心：边缘消隐遮罩 */
.celestial-mask-container {
  -webkit-mask-image: radial-gradient(circle at center, transparent 35%, black 100%);
  mask-image: radial-gradient(circle at center, transparent 35%, black 100%);
  -webkit-mask-size: cover;
  mask-size: cover;
}

/* 流星动画 */
.shooting-star {
  position: absolute;
  width: 2px;
  height: 2px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 0 20px #fff;
  animation: animate 4s linear infinite;
  opacity: 0;
}
@keyframes animate {
  0% { transform: rotate(315deg) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  30% { transform: rotate(315deg) translateX(-500px); opacity: 0; }
  100% { transform: rotate(315deg) translateX(-500px); opacity: 0; }
}
.shooting-star:nth-child(1) { top: 10%; right: 10%; animation-delay: 0s; }
.shooting-star:nth-child(2) { top: 30%; right: 20%; animation-delay: 1.5s; }
.shooting-star:nth-child(3) { top: 0; right: 40%; animation-delay: 2.5s; }

.animate-fade-in { animation: fadeIn 1s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.no-scrollbar::-webkit-scrollbar { display: none; }
</style>